// Stay-page FAQ — the plain-text source of truth for the FAQPage JSON-LD
// (Stay.jsx) and the build-time prerender (scripts/prerender.mjs), which
// cannot render JSX. The visible <Faq> component in Stay.jsx mirrors this
// wording with a real hyperlink in the Airbnb answer — keep the two in sync.

import { DIRECT_DISCOUNT_PCT } from './property-schema.js';
import { AIRBNB_GUEST_FEE_RANGE, exampleComparison } from './direct-savings.js';

// The savings answer, exported on its own because three surfaces state it (the
// FAQPage JSON-LD, the prerendered HTML, and the visible <Faq> in Stay.jsx)
// and it is the paragraph most likely to drift when pricing moves. Every
// number comes from property-config or from the shared comparison in
// direct-savings.js, the same calculation the quote card runs for real dates,
// so the worked example here and the figure on the card cannot disagree.
// Nothing here is a standing estimate except Airbnb's guest service fee,
// which is theirs and varies by stay; the FAQ states it as Airbnb's own range.
export function savingsAnswer(property) {
  const cleaningSaving =
    property.cleaningFee && property.cleaningFeeAirbnb
      ? Number((property.cleaningFeeAirbnb - property.cleaningFee).toFixed(2))
      : null;
  const ex = exampleComparison(property, 1500);
  const dollars = (n) => `$${Math.round(n).toLocaleString('en-US')}`;

  if (!cleaningSaving || !ex) {
    return `Two ways. Nightly rates are ${DIRECT_DISCOUNT_PCT}% lower when you book direct, taken off automatically in your quote with no code to enter. And Airbnb adds a guest service fee, ${AIRBNB_GUEST_FEE_RANGE} of the booking subtotal by Airbnb's own figures, that does not exist here. On a stay with $1,500 of nightly rent that is roughly $210 of service fee you skip plus $75 off the rent, close to $300 less before tax, and more once the same tax rate is applied to both. Pick dates and your quote shows an estimated Airbnb total for the same stay next to the direct total.`;
  }

  return `Three ways. Nightly rates are ${DIRECT_DISCOUNT_PCT}% lower when you book direct, taken off automatically in your quote with no code to enter. The cleaning fee is $${property.cleaningFee} here against $${property.cleaningFeeAirbnb} on Airbnb, because that one has to cover the fee Airbnb charges us and this one does not. And Airbnb adds a guest service fee, ${AIRBNB_GUEST_FEE_RANGE} of the booking subtotal by Airbnb's own figures, that does not exist here at all. On a stay with $1,500 of nightly rent, that is about ${dollars(ex.legs.guestServiceFee)} of service fee you skip at the low end, ${dollars(ex.legs.rent)} off the rent, and $${cleaningSaving} off the cleaning, about ${dollars(ex.saving)} less with the same ${property.taxRatePercent || ''}% tax applied to both, which Airbnb charges on its service fee as well. Pick dates and your quote shows an estimated Airbnb total for the same stay next to the direct total.`;
}

export function buildFaqItems(property, contactEmail) {
  return [
    {
      q: 'What happens after I click Book direct?',
      a: `A quick step asks who's coming and how to reach you. Then you're taken to our booking system's secure payment page showing the same itemized total as your quote; nothing is charged until you confirm there. Once payment completes, your dates are locked on our calendar (the one Airbnb and Vrbo sync from) and you get a confirmation right away. Check-in details like directions, lockbox, and Wi-Fi arrive before arrival.`
    },
    {
      q: 'Is this really the same home as on Airbnb?',
      a: 'Yes. Same house, same calendar, same hosts. You can compare it against our Airbnb listing side by side. Booking direct just removes the platform between us.'
    },
    {
      q: 'How much does booking direct save?',
      a: savingsAnswer(property)
    },
    ...(property.fromNightly && property.peakNightly
      ? [
          {
            q: 'What do nightly rates run?',
            a: `From about $${property.fromNightly} a night in the slow season to $${property.peakNightly} and up in peak weeks. Whatever the season, booking direct takes ${DIRECT_DISCOUNT_PCT}% off nightly rates automatically. Pick dates on the calendar for your exact total.`
          }
        ]
      : []),
    // The facts band says "Sleeps 8", which is true of the house and not of
    // every party: the booking system caps adults and children separately and
    // refuses a quote that breaks either. Better said here than discovered at
    // the quote.
    ...(property.maxAdults
      ? [
          {
            q: 'How many people can stay?',
            a: `${property.sleeps} in total, with up to ${property.maxAdults} adults and up to ${property.maxChildren} children. The bed-by-bed breakdown is in the home details above. A party larger than that won't price on the calendar, so check the numbers before you pick dates.`
          }
        ]
      : []),
    {
      q: 'Are pets allowed?',
      a: `No. The cottage is pet-free, no exceptions, which keeps it reliably comfortable for guests with allergies. Traveling with a service animal? Email ${contactEmail} before booking and we'll sort out the details.`
    },
    {
      q: 'Who do I talk to if something comes up?',
      a: `Us: ${property.hosts}. You'll have our cell from booking to checkout, and ${contactEmail} reaches us anytime. No support queue, no platform inbox.`
    }
  ];
}
